// Frontend thin wrapper — delegates to the SDK's `teardownImpersonation`
// with the Zustand store state and app-level callbacks baked in.
// Import this from frontend call sites (auth-provider, network/switcher, etc.)

import type { TeardownReason } from '@morfi/backend-sdk-v2/client';
import { teardownImpersonation as sdkTeardownImpersonation } from '@morfi/backend-sdk-v2/client';
import { TOAST_IDS } from '@/config/toast-ids';
import { translateToast } from '@/lib/i18n/toast-translator';
import { logger } from '@/lib/telemetry/logger';
import { showToast } from '@/lib/ui/toast';
import { useImpersonationStore } from './store';

export type { TeardownReason };

/**
 * Tear down the engineer-impersonation session.
 *
 * Drop-in replacement for the old `@/lib/impersonation/teardown` function.
 * Reads store state internally; wires app-level callbacks (toast, logger).
 * Idempotent — calling on a non-active session is a no-op.
 */
export async function teardownImpersonation(
  reason: TeardownReason = 'engineer_stop',
): Promise<void> {
  const state = useImpersonationStore.getState();
  await sdkTeardownImpersonation(
    {
      isActive: state.isActive,
      grantToken: state.grantToken,
      network: state.network,
      abortController: state.abortController,
    },
    {
      onSetInactive: (r: TeardownReason) => useImpersonationStore.getState().setInactive(r),
      onExpiredToast: () => {
        showToast({
          id: TOAST_IDS.IMPERSONATION_EXPIRED,
          message: translateToast('impersonation.toast.sessionExpired'),
          variant: 'warning',
        });
      },
      onRevokeError: (message: string, r: TeardownReason) => {
        logger.warn('impersonation.teardown.revoke_failed', { reason: r, message });
      },
    },
    reason,
  );
}
